import { useState, useEffect } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import {
  LayoutDashboard,
  Newspaper,
  Megaphone,
  Users,
  User,
  Disc3,
  CalendarDays,
  Video,
  ImageIcon,
  Bell,
  Search,
  ChevronDown,
  Settings2,
  BookOpen,
  ExternalLink,
  ShoppingCart,
} from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787'
const DOCS_URL = import.meta.env.VITE_DOCS_URL ?? 'http://localhost:3001'

type NavItem = { to?: string; label: string; icon: React.ElementType; adminOnly?: boolean; disabled?: boolean }
type NavGroupType = { label: string; items: NavItem[] }

const STORAGE_KEY = 'sidebar-open-groups'

const DASHBOARD_ITEM = { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true }
const SALES_HUB_ITEM = { to: '/sales', label: 'Sales Hub', icon: ShoppingCart }

const NAV_GROUPS: NavGroupType[] = [
  {
    label: 'CONTENT',
    items: [
      { to: '/news',    label: 'News',    icon: Newspaper },
      { to: '/marquee', label: 'Marquee', icon: Megaphone },
    ],
  },
  {
    label: 'ARTIST',
    items: [
      { to: '/groups',      label: 'Groups',      icon: Users },
      { to: '/members',     label: 'Members',     icon: User },
      { to: '/discography', label: 'Discography', icon: Disc3 },
    ],
  },
  {
    label: 'EVENTS',
    items: [
      { to: '/schedule', label: 'Schedule', icon: CalendarDays },
    ],
  },
  {
    label: 'MEDIA',
    items: [
      { to: '/media',  label: 'Media',  icon: Video },
      { to: '/assets', label: 'Assets', icon: ImageIcon },
    ],
  },
  {
    label: 'SYSTEM',
    items: [
      { to: '/settings', label: 'Settings', icon: Settings2, adminOnly: true },
    ],
  },
]

function NavGroup({
  group,
  isOpen,
  onToggle,
}: {
  group: NavGroupType
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-zinc-500 hover:text-zinc-400 transition-colors"
      >
        <span className="text-[10px] font-semibold tracking-widest uppercase">
          {group.label}
        </span>
        <ChevronDown
          size={16}
          className={`transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? 'max-h-96' : 'max-h-0'
        }`}
      >
        {group.items.map(({ to, label, icon: Icon, disabled }) =>
          disabled ? (
            <span
              key={label}
              className="flex items-center gap-3 pl-8 pr-3 py-2 rounded text-sm text-zinc-600 cursor-not-allowed select-none"
            >
              <Icon size={16} />
              <span className="flex-1">{label}</span>
              <span className="text-[9px] font-semibold text-zinc-700 uppercase tracking-wider">Soon</span>
            </span>
          ) : (
            <NavLink
              key={to}
              to={to!}
              className={({ isActive }) =>
                `flex items-center gap-3 pl-8 pr-3 py-2 rounded text-sm transition-colors ${
                  isActive
                    ? 'bg-zinc-800 text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                }`
              }
            >
              <Icon size={16} />
              <span>{label}</span>
            </NavLink>
          )
        )}
      </div>
    </div>
  )
}

function TopBar({ email }: { email: string }) {
  const [now, setNow] = useState(new Date())

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [])

  const dateStr = now.toLocaleDateString('id-ID', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  })

  const initials = email.slice(0, 2).toUpperCase()

  return (
    <header className="sticky top-0 z-30 h-16 bg-zinc-900 border-b border-zinc-800 flex items-center justify-between px-6 flex-shrink-0">
      <div />

      <div className="flex items-center gap-4">
        {/* Search */}
        <div className="relative hidden sm:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={14} />
          <input
            className="w-48 bg-zinc-800 text-white text-xs rounded-lg pl-8 pr-3 py-2 border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-500"
            placeholder="Search…"
            readOnly
          />
        </div>

        {/* Datetime */}
        <span className="text-xs text-zinc-500 whitespace-nowrap hidden lg:block">{dateStr}</span>

        {/* Notification bell */}
        <button
          aria-label="Notifications"
          className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
        >
          <Bell size={18} />
        </button>

        {/* User avatar */}
        <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-xs font-medium text-zinc-300 select-none">
          {initials}
        </div>
      </div>
    </header>
  )
}

export default function Layout() {
  const { user, signOut, isGroupManager } = useAuth()
  const location = useLocation()

  const navGroups = isGroupManager
    ? NAV_GROUPS.map((group) => {
        let items = group.items.filter((item) => !item.adminOnly)
        if (group.label === 'ARTIST') {
          items = items.map((item) =>
            item.to === '/groups' ? { ...item, label: 'My Group' } : item
          )
        }
        return { ...group, items }
      }).filter((group) => group.items.length > 0)
    : NAV_GROUPS

  async function handleDocsOpen() {
    const token = localStorage.getItem('admin_token')
    const res = await fetch(`${API_BASE_URL}/api/v1/admin/auth/docs-sso`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    const json = await res.json() as { success: boolean; data?: { otp: string } }
    if (json.success && json.data?.otp) {
      window.open(`${DOCS_URL}/api/auth/sso?otp=${json.data.otp}`, '_blank')
    }
  }

  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try { return new Set(JSON.parse(saved) as string[]) } catch { /* ignore corrupt data */ }
    }
    const active = NAV_GROUPS.find(g =>
      g.items.some(i => i.to && location.pathname.startsWith(i.to))
    )
    return new Set(active ? [active.label] : [])
  })

  function toggleGroup(label: string) {
    setOpenGroups(prev => {
      const next = new Set(prev)
      if (next.has(label)) { next.delete(label) } else { next.add(label) }
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]))
      return next
    })
  }

  return (
    <div className="h-screen bg-zinc-950 flex overflow-hidden">
      {/* ── Sidebar ────────────────────────────────────────────────────── */}
      <aside className="w-60 flex-shrink-0 bg-zinc-900 border-r border-zinc-800 flex flex-col">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-zinc-800">
          <span className="font-mono text-xs tracking-[0.4em] text-yellow-400">LIC</span>
          <p className="text-zinc-500 text-[11px] mt-0.5">Admin Dashboard</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {/* Dashboard — standalone, always visible */}
          <NavLink
            to={DASHBOARD_ITEM.to}
            end={DASHBOARD_ITEM.end}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors ${
                isActive
                  ? 'bg-zinc-800 text-white'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
              }`
            }
          >
            <DASHBOARD_ITEM.icon size={16} />
            <span>{DASHBOARD_ITEM.label}</span>
          </NavLink>

          {/* Sales Hub — standalone, admin only */}
          {!isGroupManager && (
            <NavLink
              to={SALES_HUB_ITEM.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors ${
                  isActive
                    ? 'bg-zinc-800 text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                }`
              }
            >
              <SALES_HUB_ITEM.icon size={16} />
              <span>{SALES_HUB_ITEM.label}</span>
            </NavLink>
          )}

          <div className="border-t border-zinc-800 my-2" />

          {/* Grouped accordion items */}
          <div className="space-y-0.5">
            {navGroups.map(group => (
              <NavGroup
                key={group.label}
                group={group}
                isOpen={openGroups.has(group.label)}
                onToggle={() => toggleGroup(group.label)}
              />
            ))}
          </div>
        </nav>

        {/* Docs link */}
        <div className="px-3 pb-3">
          <button
            onClick={handleDocsOpen}
            className="w-full flex items-center gap-3 px-3 py-2 rounded text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
          >
            <BookOpen size={16} />
            <span className="flex-1 text-left">Docs</span>
            <ExternalLink size={12} className="text-zinc-600" />
          </button>
        </div>

        {/* User */}
        <div className="px-4 py-4 border-t border-zinc-800">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] font-medium text-zinc-300 flex-shrink-0">
              {user?.email?.slice(0, 2).toUpperCase()}
            </div>
            <p className="text-[11px] text-zinc-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={() => signOut()}
            className="text-xs text-zinc-400 hover:text-white transition-colors"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Main content ───────────────────────────────────────────────── */}
      <main className="flex-1 overflow-auto flex flex-col min-w-0">
        <TopBar email={user?.email ?? ''} />
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
